import React from 'react';
import { Box, FormControl, FormGroup, FormControlLabel, Checkbox, Radio, RadioGroup, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DatePicker, DatePickerValue } from '../../date-picker/date-picker';
import { toDate } from '../../../utilities';
import { RecurrencePattern, RecurrenceWeekday } from './recurrence-pattern';
import { buildRecurrenceSummary } from './recurrence-summary';

type EndMode = 'never' | 'count' | 'until';

/**
 * Normalize a DatePicker value to the `until` ISO date string the RecurrencePattern
 * expects (YYYY-MM-DD, end-by-date inclusive). Uses local date parts so a date selected
 * in the picker is not shifted across a day boundary by UTC conversion.
 */
function datePickerValueToUntilIso(value: DatePickerValue): string | undefined {
  function pad(n: number): string {
    return String(n).padStart(2, '0');
  }
  const date = toDate(value);
  if (!date) {
    return undefined;
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

interface RecurrenceEditorProps {
  value: RecurrencePattern | undefined;
  onChange: (pattern: RecurrencePattern | undefined) => void;
  /**
   * Hides the on/off checkbox for schedules that always repeat (medicine dosage).
   * An undefined value then reads as the plain daily default, and configuring the
   * editor back to exactly that emits undefined again so nothing is stored.
   */
  alwaysRecurring?: boolean;
  /** Validation message for the "Til dato" field, decided by the owner of the surrounding form. */
  untilErrorText?: string;
}

const FREQ_UNIT_LABEL: Record<RecurrencePattern['freq'], string> = {
  daily: 'dag',
  weekly: 'uge',
  monthly: 'måned',
};

const WEEKDAYS: { token: RecurrenceWeekday; label: string }[] = [
  { token: 'mo', label: 'Man' },
  { token: 'tu', label: 'Tir' },
  { token: 'we', label: 'Ons' },
  { token: 'th', label: 'Tor' },
  { token: 'fr', label: 'Fre' },
  { token: 'sa', label: 'Lør' },
  { token: 'su', label: 'Søn' },
];

function resolveEndMode(pattern: RecurrencePattern | undefined): EndMode {
  if (!pattern) return 'never';
  if (pattern.count != null) return 'count';
  if (pattern.until != null) return 'until';
  return 'never';
}

// Dagligt uden interval og slutning ER standardadfærden; den gemmes som "intet mønster".
function isPlainDaily(pattern: RecurrencePattern): boolean {
  return (
    pattern.freq === 'daily' &&
    (pattern.interval ?? 1) === 1 &&
    pattern.count == null &&
    pattern.until == null
  );
}

/**
 * RecurrenceEditor builds a RecurrencePattern object directly from user input.
 * No iCal / RRULE string serialization happens here — the plain typed object
 * is emitted via `onChange` and passed straight to the BFF and FullCalendar mapper.
 */
export function RecurrenceEditor({ value, onChange, alwaysRecurring = false, untilErrorText }: RecurrenceEditorProps) {
  const freq = value?.freq ?? (alwaysRecurring ? 'daily' : null);
  const interval = value?.interval ?? 1;
  const byweekday = value?.byweekday ?? [];
  const bymonthday = value?.bymonthday ?? 1;
  // endMode is user-driven UI state, not derived per render: selecting "Til dato"
  // emits a pattern with no `until` yet (date not picked), so deriving the mode from
  // the value would snap the toggle back to "never" and hide the date picker.
  const [endMode, setEndMode] = React.useState<EndMode>(() => resolveEndMode(value));
  const count = value?.count ?? 10;
  const until = value?.until;

  function emitChange(patch: Partial<RecurrencePattern> & { freq: RecurrencePattern['freq'] }, nextEndMode?: EndMode) {
    const resolvedEndMode = nextEndMode ?? endMode;
    const base: RecurrencePattern = {
      freq: patch.freq,
      interval: patch.interval ?? interval,
      ...(patch.freq === 'weekly' ? { byweekday: patch.byweekday ?? byweekday } : {}),
      ...(patch.freq === 'monthly' ? { bymonthday: patch.bymonthday ?? bymonthday } : {}),
    };
    if (resolvedEndMode === 'count') {
      base.count = patch.count ?? count;
    } else if (resolvedEndMode === 'until') {
      base.until = patch.until ?? until;
    }
    if (alwaysRecurring && isPlainDaily(base)) {
      onChange(undefined);
      return;
    }
    onChange(base);
  }

  function handleRecurringToggle(checked: boolean) {
    if (checked) {
      emitChange({ freq: 'daily' });
    } else {
      onChange(undefined);
    }
  }

  function handleFreqChange(nextFreq: string | null) {
    if (!nextFreq) {
      onChange(undefined);
      return;
    }
    const f = nextFreq as RecurrencePattern['freq'];
    emitChange({ freq: f });
  }

  function handleIntervalChange(raw: string) {
    const parsed = parseInt(raw, 10);
    if (!freq || Number.isNaN(parsed) || parsed < 1) return;
    emitChange({ freq, interval: parsed });
  }

  function handleWeekdayToggle(token: RecurrenceWeekday) {
    if (freq !== 'weekly') return;
    const next = byweekday.includes(token) ? byweekday.filter(d => d !== token) : [...byweekday, token];
    emitChange({ freq: 'weekly', byweekday: next });
  }

  function handleMonthdayChange(raw: string) {
    if (freq !== 'monthly') return;
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > 31) return;
    emitChange({ freq: 'monthly', bymonthday: parsed });
  }

  function handleEndModeChange(nextMode: EndMode) {
    if (!freq) return;
    setEndMode(nextMode);
    emitChange({ freq }, nextMode);
  }

  function handleCountChange(raw: string) {
    if (!freq) return;
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed) || parsed < 1) return;
    emitChange({ freq, count: parsed }, 'count');
  }

  function handleUntilChange(pickerValue: DatePickerValue) {
    if (!freq) return;
    emitChange({ freq, until: datePickerValueToUntilIso(pickerValue) }, 'until');
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Recurring on/off */}
      {!alwaysRecurring && (
        <FormControlLabel control={<Checkbox checked={!!freq} onChange={e => handleRecurringToggle(e.target.checked)} />} label="Gentagelse" />
      )}

      {freq && (
        <>
          {/* Frequency */}
          <FormControl>
            <RadioGroup row value={freq} onChange={e => handleFreqChange(e.target.value)}>
              <FormControlLabel value="daily" control={<Radio size="small" />} label="Dagligt" />
              <FormControlLabel value="weekly" control={<Radio size="small" />} label="Ugentligt" />
              <FormControlLabel value="monthly" control={<Radio size="small" />} label="Månedligt" />
            </RadioGroup>
          </FormControl>

          {/* Interval */}
          <TextField
            size="small"
            label={`Gentag hver (${FREQ_UNIT_LABEL[freq]})`}
            type="number"
            inputProps={{ min: 1 }}
            value={interval}
            onChange={e => handleIntervalChange(e.target.value)}
          />

          {/* Weekly: weekday multi-select */}
          {freq === 'weekly' && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ugedag(e)
              </Typography>
              <FormGroup row>
                {WEEKDAYS.map(({ token, label }) => (
                  <FormControlLabel key={token} control={<Checkbox size="small" checked={byweekday.includes(token)} onChange={() => handleWeekdayToggle(token)} />} label={label} />
                ))}
              </FormGroup>
            </Box>
          )}

          {/* Monthly: day of month */}
          {freq === 'monthly' && (
            <TextField
              size="small"
              label="Dag i måneden (1–31)"
              type="number"
              inputProps={{ min: 1, max: 31 }}
              value={bymonthday}
              onChange={e => handleMonthdayChange(e.target.value)}
            />
          )}

          {/* End mode */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Slutter
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={endMode} onChange={(_, v) => v && handleEndModeChange(v as EndMode)}>
              <ToggleButton value="never">Aldrig</ToggleButton>
              <ToggleButton value="count">Efter N gange</ToggleButton>
              <ToggleButton value="until">Til dato</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {endMode === 'count' && (
            <TextField size="small" label="Antal gentagelser" type="number" inputProps={{ min: 1 }} value={count} onChange={e => handleCountChange(e.target.value)} />
          )}

          {endMode === 'until' && (
            <DatePicker
              label="Slutdato"
              value={until}
              onChange={handleUntilChange}
              variant="outlined"
              fullWidth
              error={Boolean(untilErrorText)}
              errorText={untilErrorText}
            />
          )}

          {/* Human-readable summary */}
          {(value || alwaysRecurring) && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {buildRecurrenceSummary(value ?? { freq: 'daily' })}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
